import { useMemo } from 'react';
import { historyContent } from '@data/siteData';
import { Card, CardContent } from '@components/ui/Card';
import { PDFDownloadButton } from '@components/pdf/PDFDownloadButton';
import { Calendar, Euro, Clock, Users } from 'lucide-react';
import type { Article } from '../types';

/**
 * AboutPage - Geschichte der Sternwarte
 * 
 * Demonstrates PDF export functionality with a sample article
 * based on the observatory's history content.
 */
export default function AboutPage() {
  // Create an article object from the history content for PDF export
  const historyArticle: Article = useMemo(() => {
    const content = [
      historyContent.summary,
      '',
      ...historyContent.sections.map(
        (section) => `${section.title}\n\n${section.content}`
      ),
      '',
      'Berühmte Besucher',
      '',
      'Die Mannheimer Sternwarte zählte zu ihren Ehrengästen: Wolfgang Amadeus Mozart, Thomas Jefferson, De Lalande, Lexell, Olbers, Bessel.',
    ].join('\n');

    return {
      id: 'geschichte-der-sternwarte',
      title: 'Geschichte der Sternwarte Mannheim',
      content,
      excerpt:
        'Die Alte Sternwarte Mannheim gehört zu den ältesten Gebäuden der Stadt und ist für Baden-Württemberg einzigartig. Von 1775 bis 1880 wurde hier astronomische Forschung betrieben.',
      category: 'history',
      status: 'published',
      authorName: 'Verein Stadtbild Mannheim e.V.',
      publishedAt: new Date().toISOString(),
      createdAt: '2024-01-01T00:00:00Z',
    };
  }, []);

  return (
    <div className="py-12 bg-slate-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Geschichte der Sternwarte
          </h1>
          <p className="text-lg text-slate-400">Von 1772 bis Heute</p>
        </div>

        {/* PDF Download Section */}
        <div className="mb-8 p-4 bg-slate-900/50 border border-slate-800 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-white font-semibold mb-1">
              Artikel als PDF herunterladen
            </h3>
            <p className="text-slate-400 text-sm">
              Speichern Sie die Geschichte der Sternwarte als PDF-Dokument
            </p>
          </div>
          <PDFDownloadButton
            article={historyArticle}
            buttonText="PDF herunterladen"
            variant="outline"
            size="md"
            onGenerateStart={() => console.log('PDF generation started')}
            onGenerateComplete={(filename) =>
              console.log(`PDF generated: ${filename}`)
            }
            onGenerateError={(error) =>
              console.error('PDF generation failed:', error)
            }
          />
        </div>

        {/* Introduction */}
        <div className="prose prose-invert prose-lg max-w-none mb-12">
          <p className="text-slate-300 leading-relaxed">
            Die Alte Sternwarte Mannheim gehört zu den ältesten Gebäuden der Stadt und ist für
            Baden-Württemberg einzigartig. Von 1775 bis 1880 wurde hier astronomische Forschung
            betrieben, die besonders unter dem Astronomen Christian Mayer in der Ära des
            Kurfürsten Carl Theodor (1742 – 1799) zu einem &quot;Markenzeichen&quot; wurde.
          </p>
        </div>

        {/* Key Facts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          <Card>
            <CardContent className="flex items-center gap-4 py-4">
              <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center shrink-0">
                <Calendar className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Gründung</p>
                <p className="text-white font-semibold">1772</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 py-4">
              <div className="w-12 h-12 bg-green-600/10 rounded-lg flex items-center justify-center shrink-0">
                <Euro className="text-green-400" size={24} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Baukosten</p>
                <p className="text-white font-semibold">ca. 102.000 Gulden</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 py-4">
              <div className="w-12 h-12 bg-purple-600/10 rounded-lg flex items-center justify-center shrink-0">
                <Clock className="text-purple-400" size={24} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Forschungszeitraum</p>
                <p className="text-white font-semibold">1775 – 1880</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 py-4">
              <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center shrink-0">
                <Users className="text-amber-400" size={24} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Berühmte Besucher</p>
                <p className="text-white font-semibold">Mozart, Jefferson u.v.m.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* History Sections */}
        <div className="space-y-8">
          {historyContent.sections.map((section, index) => (
            <section key={index} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
              <p className="text-slate-300 leading-relaxed">{section.content}</p>
            </section>
          ))}
        </div>

        {/* Famous Visitors */}
        <section className="mt-12 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-xl p-6 border border-blue-800/30">
          <h2 className="text-xl font-bold text-white mb-4">Berühmte Besucher</h2>
          <p className="text-slate-300 mb-4">
            Die Mannheimer Sternwarte zählte zu ihren Ehrengästen:
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              'Wolfgang Amadeus Mozart',
              'Thomas Jefferson',
              'De Lalande',
              'Lexell',
              'Olbers',
              'Bessel',
            ].map((visitor) => (
              <span
                key={visitor}
                className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-sm"
              >
                {visitor}
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
